/** Shape returned by api/users/details for the user's timezone. */
export interface UserTimezone {
  keyName?: string;
  timeZoneID?: number;
  displayName?: string;
}

export type DateFormatMode =
  | 'date'
  | 'dateTime'
  | 'dateTimeWithSeconds'
  | 'dateWithYear'
  | 'dateTimeWithYear'
  | 'dateTimeYearWithSeconds';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const parseOffsetMinutes = (displayName: string): number => {
  const match = displayName.match(/GMT([+-])(\d{2}):(\d{2})/);
  if (!match) return 0;
  const sign  = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2] ?? '0', 10);
  const mins  = parseInt(match[3] ?? '0', 10);
  return sign * (hours * 60 + mins);
};

const getIanaOffsetMinutes = (ianaZone: string, utcMs: number): number => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: ianaZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(new Date(utcMs));
  const v = (t: string) => parseInt(parts.find(p => p.type === t)?.value ?? '0', 10);
  const h = v('hour') % 24;
  const localMs = Date.UTC(v('year'), v('month') - 1, v('day'), h, v('minute'), v('second'));
  return (localMs - utcMs) / 60_000;
};

// In-memory cache: lives only for the lifetime of the current browser tab /
// app process.  It is never persisted, so there is no version-bumping concern.
const ianaCache: Record<string, string | null> = {};

const resolveIanaZone = (
  keyName: string,
  stdOffsetMinutes: number,
  displayName: string,
): string | null => {
  if (keyName in ianaCache) {
    return ianaCache[keyName] ?? null;
  }

  try {
    const supportedFn = (Intl as unknown as Record<string, unknown>).supportedValuesOf;
    if (typeof supportedFn !== 'function') {
      ianaCache[keyName] = null;
      return null;
    }

    const allZones = (supportedFn as (key: string) => string[])('timeZone');

    const janProbeMs = Date.UTC(new Date().getUTCFullYear(), 0, 15, 12, 0, 0);
    const julProbeMs = Date.UTC(new Date().getUTCFullYear(), 6, 15, 12, 0, 0);

    const candidates = allZones.filter(zone => {
      try {
        return getIanaOffsetMinutes(zone, janProbeMs) === stdOffsetMinutes;
      } catch {
        return false;
      }
    });

    if (!candidates.length) {
      ianaCache[keyName] = null;
      return null;
    }

    const hintWords = displayName
      .replace(/[()+-]/g, ' ')
      .toLowerCase()
      .split(/[\s,&/]+/)
      .filter(w => w.length > 3 && !/^(gmt|utc|\d)/.test(w));

    // keyName token set for Tiebreaker A.
    // "Hawaiian Standard Time" → {"hawaiian","standard","time"}
    const keyNameTokens = new Set(
      keyName.toLowerCase().split(/[\s-]+/).filter(Boolean)
    );

    // Acronym from keyName used to match timezone abbreviations.
    // "Hawaiian Standard Time" → "HST", "Central Standard Time" → "CST", etc.
    const keyAcronym = keyName.split(/\s+/).map(w => w[0]?.toUpperCase() ?? '').join('');

    let bestZone: string  = candidates[0]!;
    let bestScore         = -1;
    let bestShortScore    = -1;
    let bestExtraCount    = Infinity;
    let bestIsDst         = false;

    for (const zone of candidates) {
      try {
        const dtfJan = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'long' });
        const longNameRaw = dtfJan
          .formatToParts(new Date(janProbeMs))
          .find(p => p.type === 'timeZoneName')
          ?.value ?? '';

        // Score: hint words in (January long name + zone identifier)
        const combined = longNameRaw.toLowerCase() + ' ' + zone.toLowerCase();
        const score = hintWords.filter(w => combined.includes(w)).length;

        // Tiebreaker A: short-name abbreviation match against keyName acronym.
        // Pacific/Honolulu → "HST" matches keyAcronym "HST" for "Hawaiian Standard Time".
        // America/Adak    → "HAST" does NOT match → Honolulu wins in modern Chrome
        // where both zones share the same long name "Hawaii-Aleutian Standard Time".
        const shortNameRaw = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
          .formatToParts(new Date(janProbeMs))
          .find(p => p.type === 'timeZoneName')
          ?.value ?? '';
        const shortScore = shortNameRaw === keyAcronym ? 1 : 0;

        // Tiebreaker B: long-name tokens absent from keyNameTokens (fewer = better)
        const longNameTokens = longNameRaw.toLowerCase().split(/[\s-]+/).filter(Boolean);
        const extraCount = longNameTokens.filter(t => !keyNameTokens.has(t)).length;

        // Tiebreaker C: prefer DST-observing zone (handles Central/Eastern/Mountain/Pacific)
        let isDst = false;
        try {
          isDst = getIanaOffsetMinutes(zone, julProbeMs) !== stdOffsetMinutes;
        } catch { /* treat as non-DST */ }

        const beats =
          score > bestScore ||
          (score === bestScore && shortScore > bestShortScore) ||
          (score === bestScore && shortScore === bestShortScore && extraCount < bestExtraCount) ||
          (score === bestScore && shortScore === bestShortScore && extraCount === bestExtraCount && isDst && !bestIsDst);

        if (beats) {
          bestScore      = score;
          bestShortScore = shortScore;
          bestZone       = zone;
          bestExtraCount = extraCount;
          bestIsDst      = isDst;
        }
      } catch { /* skip zone */ }
    }

    // Only return a dynamic IANA zone when the best-matching candidate observes DST.
    // If the winner is non-DST (Pacific/Honolulu for "Hawaiian Standard Time"),
    // the static offset from displayName is always correct — return null so the
    // static fallback is used.  This also fires when no hint word matched (bestScore ≤ 0),
    // which is correct for Arizona, Saskatchewan, India, Nepal, etc.
    if (bestScore <= 0 || !bestIsDst) {
      ianaCache[keyName] = null;
      return null;
    }

    ianaCache[keyName] = bestZone;
    return bestZone;
  } catch {
    ianaCache[keyName] = null;
    return null;
  }
};

export const formatDate = (
  apiDate: string,
  timezone: UserTimezone,
  mode: DateFormatMode = 'dateTime',
): string => {
  if (!apiDate) return '';

  const utcString = /Z$|[+-]\d{2}:\d{2}$/.test(apiDate) ? apiDate : `${apiDate}Z`;
  const utcMs = Date.parse(utcString);
  if (isNaN(utcMs)) return '';

  const stdOffsetMin = parseOffsetMinutes(timezone.displayName ?? '');

  const ianaZone = timezone.keyName
    ? resolveIanaZone(timezone.keyName, stdOffsetMin, timezone.displayName ?? '')
    : null;

  if (ianaZone) {
    try {
      const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: ianaZone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      });
      const parts = dtf.formatToParts(new Date(utcMs));
      const v = (t: string) => parts.find(p => p.type === t)?.value ?? '00';
      const h24  = parseInt(v('hour'), 10) % 24;
      const min  = v('minute');
      const sec  = v('second');
      const day  = v('day');
      const yr   = v('year');
      const mon  = MONTHS[parseInt(v('month'), 10) - 1] ?? '';
      const ampm = h24 >= 12 ? 'PM' : 'AM';
      const h12  = String(h24 % 12 || 12).padStart(2, '0');

      switch (mode) {
        case 'date':                    return `${mon} ${day}`;
        case 'dateTimeWithSeconds':     return `${mon} ${day}, ${h12}:${min}:${sec} ${ampm}`;
        case 'dateWithYear':            return `${mon} ${day}, ${yr}`;
        case 'dateTimeWithYear':        return `${mon} ${day}, ${yr} ${h12}:${min} ${ampm}`;
        case 'dateTimeYearWithSeconds': return `${mon} ${day}, ${yr} ${h12}:${min}:${sec} ${ampm}`;
        case 'dateTime':
        default:                        return `${mon} ${day}, ${h12}:${min} ${ampm}`;
      }
    } catch { /* fall through to static path */ }
  }

  // Static offset fallback (no DST correction)
  const local = new Date(utcMs + stdOffsetMin * 60_000);
  const day  = String(local.getUTCDate()).padStart(2, '0');
  const mon  = MONTHS[local.getUTCMonth()] ?? '';
  const yr   = local.getUTCFullYear();
  const h24  = local.getUTCHours();
  const min  = String(local.getUTCMinutes()).padStart(2, '0');
  const sec  = String(local.getUTCSeconds()).padStart(2, '0');
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  const h12  = String(h24 % 12 || 12).padStart(2, '0');

  switch (mode) {
    case 'date':                    return `${mon} ${day}`;
    case 'dateTimeWithSeconds':     return `${mon} ${day}, ${h12}:${min}:${sec} ${ampm}`;
    case 'dateWithYear':            return `${mon} ${day}, ${yr}`;
    case 'dateTimeWithYear':        return `${mon} ${day}, ${yr} ${h12}:${min} ${ampm}`;
    case 'dateTimeYearWithSeconds': return `${mon} ${day}, ${yr} ${h12}:${min}:${sec} ${ampm}`;
    case 'dateTime':
    default:                        return `${mon} ${day}, ${h12}:${min} ${ampm}`;
  }
};